import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Grid,
    Paper,
    Slide,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip as TooltipMui,
    Typography,
    Zoom,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { TransitionProps } from '@mui/material/transitions';
import React, { useEffect, useState } from 'react';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Individual } from '../../../utils';
import { displayDistanceByUnit } from '../community.func';
import { ChartData, PerformanceDataUpload, Sprint } from '../community.types';
import ChartGradientPaper from '../components/Charting/ChartGradientPaperContainer';
import {
    useSpeedUnitPreference,
    convertMpsToPreferred,
    type PreferredSpeedUnit,
} from 'hooks/useSpeedUnitPreference';

interface Props {
    individual: Individual;
    upload: PerformanceDataUpload;
    sprint: Sprint;
    isMobileDevice: boolean;
    distanceUnit: 'm' | 'yd' | 'ft';
    hideFVP?: boolean;
    sideBySide?: boolean;
    onDeleted: () => void;
    sport: string;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function displayMpsToPreferred(mps: number | null, unit: PreferredSpeedUnit = 'mph') {
    return mps ? convertMpsToPreferred(mps, unit).toFixed(2) : '-';
}

function getSplitTimeFromSprint(sprint: Sprint, start: number, end: number) {
    const splitTimes = sprint.all_splits;
    if (!splitTimes) return null;

    const splitTime = splitTimes.find(
        (split) => split.start_distance === start && split.end_distance === end,
    );

    return splitTime?.split_time ? splitTime.split_time.toFixed(2) : null;
}

function getSplitTimeFromSprintMetric(sprint: Sprint, start: number, end: number) {
    const splitTimes = sprint.all_splits_meters;
    if (!splitTimes) return null;

    const splitTime = splitTimes.find(
        (split) => split.start_distance === start && split.end_distance === end,
    );

    return splitTime?.split_time ? splitTime.split_time.toFixed(2) : null;
}

function getSplitTimeFromSprintFeet(sprint: Sprint, start: number, end: number) {
    const splitTimes = sprint.all_splits_feet;
    if (!splitTimes) return null;

    const splitTime = splitTimes.find(
        (split) => split.start_distance === start && split.end_distance === end,
    );

    return splitTime?.split_time ? splitTime.split_time.toFixed(2) : null;
}

function getDistanceConversionFactor(distanceUnit: string) {
    switch (distanceUnit) {
        case 'yd':
            return 1.09361; // meters to yards
        case 'ft':
            return 3.28084; // meters to feet
        case 'm':
        default:
            return 1; // meters to meters (no conversion)
    }
}

function getDistanceInUnit(valueInMeters: number, unit: 'm' | 'yd' | 'ft'): number {
    switch (unit) {
        case 'yd':
            return valueInMeters * 1.09361;
        case 'ft':
            return valueInMeters * 3.28084;
        case 'm':
        default:
            return valueInMeters;
    }
}

function getStandardizedDistanceValue(
    value: number,
    fromUnit: 'm' | 'yd' | 'ft',
    toUnit: 'm' | 'yd' | 'ft',
): number {
    // First convert to meters (standard unit)
    let valueInMeters;
    switch (fromUnit) {
        case 'yd':
            valueInMeters = value / 1.09361;
            break;
        case 'ft':
            valueInMeters = value / 3.28084;
            break;
        case 'm':
        default:
            valueInMeters = value;
    }

    // Then convert to target unit
    return getDistanceInUnit(valueInMeters, toUnit);
}

function ForceVelocityProfileSprint({
    sprint,
    isMobileDevice,
    distanceUnit,
    hideFVP,
    sideBySide,
    sport,
}: Props) {
    const { unit: speedUnit, label: speedLabel } = useSpeedUnitPreference();
    const [openModal, setOpenModal] = React.useState(false);
    const [data, setData] = useState<ChartData[]>([]);
    const [decelData, setDecelData] = useState<any[]>([]);
    const [YaxisMax, setYaxisMax] = useState<number>(0);

    useEffect(() => {
        setYaxisMax(Math.max(...data.map((d) => d['Velocity (m/s)'])) + 3);
    }, [data]);

    useEffect(() => {
        if (!sprint) return;

        const conversionFactor = getDistanceConversionFactor(distanceUnit);
        const distanceKey = `Cumulative Distance (${distanceUnit})`;

        // generate fvp - time data
        const speedFactor = speedUnit === 'fps' ? 3.28084 : 2.23694;
        setData(
            sprint.raw_data && sprint.raw_data.length > 0
                ? sprint.raw_data.map((dataElement) => ({
                      'Adjusted Time (s)': parseFloat(dataElement['Adjusted Time (s)'].toFixed(2)),
                      'Velocity (m/s)': +(
                          Math.abs(dataElement['Velocity (m/s)']) * speedFactor
                      ).toFixed(2),
                      'Model Velocity (m/s)': +(
                          dataElement['Model Velocity (m/s)'] * speedFactor
                      ).toFixed(3),
                      'Model Acceleration (m/s2)':
                          +dataElement['Model Acceleration (m/s2)'].toFixed(2),
                      'Model Force (N)': +dataElement['Model Force (N)'].toFixed(3),
                      'Model Force (N/kg)': +dataElement['Model Force (N/kg)'].toFixed(3),
                      'Model Power (W/kg)': +dataElement['Model Power (W/kg)'].toFixed(2),
                      [distanceKey]: dataElement['Cumulative Distance (m)']
                          ? +(+dataElement['Cumulative Distance (m)'] * conversionFactor).toFixed(2)
                          : 0,
                      'Model Ratio Force (N)': +(
                          dataElement['Model Ratio Force (N)'] * 100
                      ).toFixed(1),
                  }))
                : [],
        );

        // generate deceleration data
        setDecelData(
            sprint.decel_raw_data && sprint.decel_raw_data.length > 0
                ? sprint.decel_raw_data.map((dataElement) => ({
                      'Adjusted Time (s)': +dataElement['Adjusted Time (s)'].toFixed(2),
                      'Velocity Filtered (m/s)': +(
                          dataElement['Velocity Filtered (m/s)'] * speedFactor
                      ).toFixed(2),
                      [distanceKey]: dataElement['Cumulative Distance (m)']
                          ? +(+dataElement['Cumulative Distance (m)'] * conversionFactor).toFixed(2)
                          : 0,
                  }))
                : [],
        );
    }, [sprint, distanceUnit, speedUnit]);

    const handleClickOpen = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const selectedSportIs = (sports: string[]) => {
        return sports.includes(sport) || sport === 'all';
    };

    function getDistanceUnitLabel(distanceUnit: 'm' | 'yd' | 'ft'): string {
        return distanceUnit;
    }

    // Generate the dynamic distance key for chart components
    const distanceKey = `Cumulative Distance (${getDistanceUnitLabel(distanceUnit)})`;

    return (
        <>
            {hideFVP && (
                <Stack direction={'column'} sx={{ padding: 4 }} spacing={4}>
                    {selectedSportIs(['baseball']) &&
                        (() => {
                            const isStandstillStart =
                                !sprint.initial_velocity_raw || sprint.initial_velocity_raw < 0.75;
                            const split79 = isStandstillStart
                                ? getSplitTimeFromSprintFeet(sprint, 0, 79)
                                : null;
                            const split90 = isStandstillStart
                                ? getSplitTimeFromSprintFeet(sprint, 0, 90) ||
                                  getSplitTimeFromSprint(sprint, 0, 30)
                                : null;
                            const reaction = isStandstillStart
                                ? sprint['jump_distance_react_ft']
                                : null;
                            const burst = isStandstillStart
                                ? sprint['jump_distance_burst_ft']
                                : null;
                            const jump =
                                reaction && burst
                                    ? (Number(reaction) + Number(burst)).toFixed(2)
                                    : null;

                            const cardSx = {
                                backgroundColor: '#D9DADF',
                                borderRadius: 2,
                                border: '1px solid #A7ABB7',
                                background:
                                    'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                p: 2,
                            };
                            const labelSx = {
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                fontWeight: 600,
                                textAlign: 'center' as const,
                            };
                            const valueSx = {
                                fontSize: '1.25rem',
                                fontWeight: 700,
                                mt: 0.5,
                                textAlign: 'center' as const,
                            };
                            const startSpeedDisplay = sprint.initial_velocity_raw
                                ? convertMpsToPreferred(
                                      sprint.initial_velocity_raw,
                                      speedUnit,
                                  ).toFixed(2)
                                : null;

                            return (
                                <Grid container spacing={2}>
                                    {!isStandstillStart && startSpeedDisplay && (
                                        <Grid item xs={12}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    px: 1,
                                                }}
                                            >
                                                <InfoIcon
                                                    fontSize="small"
                                                    sx={{ color: '#e6a817' }}
                                                />
                                                <Typography variant="body2">
                                                    Split times hidden — starting speed was{' '}
                                                    {startSpeedDisplay} {speedLabel} (standstill
                                                    threshold:{' '}
                                                    {convertMpsToPreferred(0.75, speedUnit).toFixed(
                                                        2,
                                                    )}{' '}
                                                    {speedLabel}).
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                    {/* Card 1: Top Speed / Total Distance / Top Speed within 120' */}
                                    <Grid item xs={12} md={4}>
                                        <Paper sx={cardSx} elevation={0}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    mb: 1.5,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                Speed &amp; Distance
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={labelSx}>Top Speed</Typography>
                                                    <Typography sx={valueSx}>
                                                        {convertMpsToPreferred(
                                                            sprint.v_max,
                                                            speedUnit,
                                                        ).toFixed(2)}{' '}
                                                        {speedLabel}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={labelSx}>
                                                        Total Distance
                                                    </Typography>
                                                    <Typography sx={valueSx}>
                                                        {sprint.total_distance_traveled
                                                            ? displayDistanceByUnit(
                                                                  sprint.total_distance_traveled,
                                                                  distanceUnit,
                                                              )
                                                            : '-'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={labelSx}>
                                                        Top Speed within{' '}
                                                        {getStandardizedDistanceValue(
                                                            40,
                                                            'yd',
                                                            distanceUnit,
                                                        ).toFixed(0)}
                                                        {distanceUnit === 'ft'
                                                            ? "'"
                                                            : ` ${distanceUnit}`}
                                                    </Typography>
                                                    <Typography sx={valueSx}>
                                                        {displayMpsToPreferred(
                                                            sprint.max_v_0_to_40,
                                                            speedUnit,
                                                        )}{' '}
                                                        {speedLabel}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>

                                    {/* Card 2: 79' / 90' split times */}
                                    {(split79 || split90) && (
                                        <Grid item xs={12} md={4}>
                                            <Paper sx={cardSx} elevation={0}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        mb: 1.5,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Split Times
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    {split79 && (
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography sx={labelSx}>
                                                                79&apos; (s)
                                                            </Typography>
                                                            <Typography sx={valueSx}>
                                                                {split79}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {split90 && (
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography sx={labelSx}>
                                                                90&apos; (s)
                                                            </Typography>
                                                            <Typography sx={valueSx}>
                                                                {split90}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    )}

                                    {/* Card 3: Reaction / Burst / Jump */}
                                    {(reaction || burst || jump) && (
                                        <Grid item xs={12} md={4}>
                                            <Paper sx={cardSx} elevation={0}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        mb: 1.5,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Jump Metrics
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    {reaction && (
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography sx={labelSx}>
                                                                Reaction 0-1.5 (s)
                                                            </Typography>
                                                            <Typography sx={valueSx}>
                                                                {reaction}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {burst && (
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography sx={labelSx}>
                                                                Burst 1.5-3 (s)
                                                            </Typography>
                                                            <Typography sx={valueSx}>
                                                                {burst}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {jump && (
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography sx={labelSx}>
                                                                Jump 0-3 (s)
                                                            </Typography>
                                                            <Typography sx={valueSx}>
                                                                {jump}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            );
                        })()}

                    {/* Non-baseball fallback: original Vmax / Total Distance / Vmax (120) table */}
                    {!selectedSportIs(['baseball']) && (
                        <TableContainer
                            sx={{
                                backgroundColor: '#D9DADF',
                                borderRadius: 4,
                                border: '1px solid #A7ABB7',
                                background:
                                    'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            borderBottom: '1.5px solid #A7ABB7',
                                        }}
                                    >
                                        <TooltipMui
                                            disableFocusListener
                                            title="Maximum speed reached during this sprint measured directly from GPS unit."
                                            placement="top-start"
                                            TransitionComponent={Zoom}
                                        >
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                Top Speed
                                            </TableCell>
                                        </TooltipMui>

                                        <TooltipMui
                                            disableFocusListener
                                            title="Total distance traveled during the sprint"
                                            placement="top-start"
                                            TransitionComponent={Zoom}
                                        >
                                            <TableCell>Total Distance</TableCell>
                                        </TooltipMui>
                                        {selectedSportIs(['football']) && (
                                            <TooltipMui
                                                disableFocusListener
                                                title="Max speed reached during the first 40 yards"
                                                placement="top-start"
                                                TransitionComponent={Zoom}
                                            >
                                                <TableCell>
                                                    Top Speed within{' '}
                                                    {getStandardizedDistanceValue(
                                                        40,
                                                        'yd',
                                                        distanceUnit,
                                                    ).toFixed(0)}{' '}
                                                    {distanceUnit}
                                                </TableCell>
                                            </TooltipMui>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>
                                            {convertMpsToPreferred(sprint.v_max, speedUnit).toFixed(
                                                2,
                                            )}{' '}
                                            {speedLabel}
                                        </TableCell>
                                        <TableCell>
                                            {!sprint.total_distance_traveled && '-'}
                                            {sprint.total_distance_traveled &&
                                                displayDistanceByUnit(
                                                    sprint.total_distance_traveled,
                                                    distanceUnit,
                                                )}
                                        </TableCell>
                                        {selectedSportIs(['football']) && (
                                            <TableCell>
                                                {displayMpsToPreferred(
                                                    sprint.max_v_0_to_40,
                                                    speedUnit,
                                                )}{' '}
                                                {speedLabel}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {sprint.avg_velocity_grouped_by_second_data.length > 0 &&
                        selectedSportIs(['baseball', 'football']) && (
                            <TableContainer
                                sx={{
                                    backgroundColor: '#D9DADF',
                                    borderRadius: 4,
                                    border: '1px solid #A7ABB7',
                                    background:
                                        'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                Time Segment:
                                            </TableCell>
                                            {sprint.avg_velocity_grouped_by_second_data.map(
                                                (r: any, index: number) => (
                                                    <TableCell
                                                        sx={{ fontWeight: 'bold' }}
                                                        key={index}
                                                    >
                                                        {r['Adjusted Time Rounded (s)']}-
                                                        {r['Adjusted Time Rounded (s)'] + 0.99}s
                                                    </TableCell>
                                                ),
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow sx={{ borderBottom: '1.5px solid #A7ABB7' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                Avg Speed:
                                            </TableCell>
                                            {sprint.avg_velocity_grouped_by_second_data.map(
                                                (r: any, index: number) => (
                                                    <TableCell key={index}>
                                                        {convertMpsToPreferred(
                                                            r['Velocity Filtered (m/s)'],
                                                            speedUnit,
                                                        ).toFixed(2)}{' '}
                                                        {speedLabel}
                                                    </TableCell>
                                                ),
                                            )}
                                        </TableRow>
                                        <TableRow sx={{ borderBottom: '1.5px solid #A7ABB7' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                Distance:
                                            </TableCell>
                                            {sprint.distance_grouped_by_second.map(
                                                (r: any, index: number) => (
                                                    <TableCell key={index}>
                                                        {displayDistanceByUnit(
                                                            r['Distance (m)'],
                                                            distanceUnit,
                                                        )}
                                                    </TableCell>
                                                ),
                                            )}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    {selectedSportIs(['baseball', 'football']) &&
                        (() => {
                            const totalDistMeters =
                                parseFloat(String(sprint.total_distance_traveled)) || 0;
                            const show40to50 = totalDistMeters >= 45.72;
                            const show40plus = totalDistMeters >= 36.58;
                            const isStandstillStart =
                                !sprint.initial_velocity_raw || sprint.initial_velocity_raw < 0.75;
                            const startSpeedDisplay = sprint.initial_velocity_raw
                                ? convertMpsToPreferred(
                                      sprint.initial_velocity_raw,
                                      speedUnit,
                                  ).toFixed(2)
                                : null;

                            return (
                                <>
                                    {!isStandstillStart && startSpeedDisplay && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1,
                                            }}
                                        >
                                            <InfoIcon fontSize="small" sx={{ color: '#e6a817' }} />
                                            <Typography variant="body2">
                                                Split times hidden — starting speed was{' '}
                                                {startSpeedDisplay} {speedLabel} (standstill
                                                threshold:{' '}
                                                {convertMpsToPreferred(0.75, speedUnit).toFixed(2)}{' '}
                                                {speedLabel}).
                                            </Typography>
                                        </Box>
                                    )}
                                    <TableContainer
                                        sx={{
                                            backgroundColor: '#D9DADF',
                                            borderRadius: 4,
                                            border: '1px solid #A7ABB7',
                                            background:
                                                'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                        }}
                                    >
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        Distance (
                                                        {distanceUnit === 'ft' ? 'ft' : 'yd'})
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        0-{distanceUnit === 'ft' ? '30' : '10'}
                                                        {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        0-{distanceUnit === 'ft' ? '60' : '20'}
                                                        {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        0-{distanceUnit === 'ft' ? '75' : '25'}
                                                        {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        0-{distanceUnit === 'ft' ? '90' : '30'}
                                                        {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        0-{distanceUnit === 'ft' ? '105' : '35'}
                                                        {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        0-{distanceUnit === 'ft' ? '120' : '40'}
                                                        {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        {distanceUnit === 'ft' ? '30' : '10'}-
                                                        {distanceUnit === 'ft' ? '60' : '20'}
                                                        {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        {distanceUnit === 'ft' ? '60' : '20'}-
                                                        {distanceUnit === 'ft' ? '90' : '30'}
                                                        {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        {distanceUnit === 'ft' ? '90' : '30'}-
                                                        {distanceUnit === 'ft' ? '120' : '40'}
                                                        {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                    </TableCell>
                                                    {show40to50 && (
                                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                                            {distanceUnit === 'ft' ? '120' : '40'}-
                                                            {distanceUnit === 'ft' ? '150' : '50'}
                                                            {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                        </TableCell>
                                                    )}
                                                    {show40plus && (
                                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                                            {distanceUnit === 'ft' ? '120' : '40'}+
                                                            {distanceUnit === 'ft' ? 'ft' : 'yd'}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow
                                                    sx={{ borderBottom: '1.5px solid #A7ABB7' }}
                                                >
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        Best 5m Avg Speed ({speedLabel})
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_5m_0_to_10,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_5m_0_to_20,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_5m_0_to_25,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_5m_0_to_30,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_5m_0_to_35,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_5m_0_to_40,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_5m_10_to_20,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_5m_20_to_30,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_5m_30_to_40,
                                                        )}
                                                    </TableCell>
                                                    {show40to50 && (
                                                        <TableCell>
                                                            {displayMpsToPreferred(
                                                                sprint.max_5m_40_to_50,
                                                            )}
                                                        </TableCell>
                                                    )}
                                                    {show40plus && (
                                                        <TableCell>
                                                            {displayMpsToPreferred(
                                                                sprint.max_5m_40_to_inf,
                                                            )}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                                <TableRow
                                                    sx={{ borderBottom: '1.5px solid #A7ABB7' }}
                                                >
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        Avg Speed ({speedLabel})
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_avg_v_0_to_10,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_avg_v_0_to_20,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_avg_v_0_to_25,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_avg_v_0_to_30,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_avg_v_0_to_35,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_avg_v_0_to_40,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_avg_v_10_to_20,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_avg_v_20_to_30,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayMpsToPreferred(
                                                            sprint.max_avg_v_30_to_40,
                                                        )}
                                                    </TableCell>
                                                    {show40to50 && (
                                                        <TableCell>
                                                            {displayMpsToPreferred(
                                                                sprint.max_avg_v_40_to_50,
                                                            )}
                                                        </TableCell>
                                                    )}
                                                    {show40plus && (
                                                        <TableCell>
                                                            {displayMpsToPreferred(
                                                                sprint.max_5m_40_to_inf,
                                                            )}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                                <TableRow
                                                    sx={{ borderBottom: '1.5px solid #A7ABB7' }}
                                                >
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        Split Times (s)
                                                    </TableCell>
                                                    <TableCell>
                                                        {isStandstillStart
                                                            ? getSplitTimeFromSprint(
                                                                  sprint,
                                                                  0,
                                                                  10,
                                                              ) || '-'
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {isStandstillStart
                                                            ? getSplitTimeFromSprint(
                                                                  sprint,
                                                                  0,
                                                                  20,
                                                              ) || '-'
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {isStandstillStart
                                                            ? getSplitTimeFromSprint(
                                                                  sprint,
                                                                  0,
                                                                  25,
                                                              ) || '-'
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {isStandstillStart
                                                            ? getSplitTimeFromSprint(
                                                                  sprint,
                                                                  0,
                                                                  30,
                                                              ) || '-'
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {isStandstillStart
                                                            ? getSplitTimeFromSprint(
                                                                  sprint,
                                                                  0,
                                                                  35,
                                                              ) || '-'
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {isStandstillStart
                                                            ? getSplitTimeFromSprint(
                                                                  sprint,
                                                                  0,
                                                                  40,
                                                              ) || '-'
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {getSplitTimeFromSprint(sprint, 10, 20) ||
                                                            '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {getSplitTimeFromSprint(sprint, 20, 30) ||
                                                            '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {getSplitTimeFromSprint(sprint, 30, 40) ||
                                                            '-'}
                                                    </TableCell>
                                                    {show40to50 && (
                                                        <TableCell>
                                                            {getSplitTimeFromSprint(
                                                                sprint,
                                                                40,
                                                                50,
                                                            ) || '-'}
                                                        </TableCell>
                                                    )}
                                                    {show40plus && (
                                                        <TableCell>
                                                            {isStandstillStart
                                                                ? getSplitTimeFromSprint(
                                                                      sprint,
                                                                      0,
                                                                      50,
                                                                  ) || '-'
                                                                : '-'}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            );
                        })()}

                    {selectedSportIs(['track']) && (
                        <TableContainer
                            sx={{
                                backgroundColor: '#D9DADF',
                                borderRadius: 4,
                                border: '1px solid #A7ABB7',
                                background:
                                    'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            Distance (m)
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            0-10m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            0-15m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            0-20m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            0-25m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            0-30m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            0-35m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            0-40m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            0-100m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            0-200m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            0-400m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            10-20m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            20-30m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            20-40m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            30-40m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            40-50m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            50-60m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            60-70m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            70-80m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            80-90m
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                                            90-100m
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow sx={{ borderBottom: '1.5px solid #A7ABB7' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            Split Times (s)
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 0, 10) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 0, 15) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 0, 20) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 0, 25) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 0, 30) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 0, 35) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 0, 40) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 0, 100) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 0, 200) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 0, 400) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 10, 20) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 20, 30) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 20, 40) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 30, 40) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 40, 50) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 50, 60) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 60, 70) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 70, 80) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 80, 90) || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {getSplitTimeFromSprintMetric(sprint, 90, 100) || '-'}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Stack>
            )}
            {!hideFVP && (
                <Box sx={{ padding: 4 }}>
                    <TableContainer
                        sx={{
                            backgroundColor: '#D9DADF',
                            borderRadius: 4,
                            border: '1px solid #A7ABB7',
                            marginBottom: 5,
                            background:
                                'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ borderBottom: '1.5px solid #A7ABB7' }}>
                                    {/** if there isn't FVP data then
                                     sprint.f_0 will be false for all fvp
                                     related metrics**/}
                                    {sprint.f_0 && (
                                        <>
                                            <TooltipMui
                                                disableFocusListener
                                                title="Maximal force output (per unit body mass) in the
                                            horizontal direction. Corresponds to the initial push of
                                            the athlete onto the ground during sprint acceleration.
                                            The higher the value, the higher the sprint-specific
                                            horizontal force production."
                                                placement="top-start"
                                                TransitionComponent={Zoom}
                                            >
                                                <TableCell>F0/kg</TableCell>
                                            </TooltipMui>

                                            <TooltipMui
                                                disableFocusListener
                                                title=" Maximal power-output capability of the athlete in the
                                            horizontal direction (per unit body mass) during sprint
                                            acceleration."
                                                placement="top-start"
                                                TransitionComponent={Zoom}
                                            >
                                                <TableCell>PMax</TableCell>
                                            </TooltipMui>
                                            <TooltipMui
                                                disableFocusListener
                                                title="Sprint-running maximal velocity capability of the
                                            athlete. Slightly higher than the actual maximal
                                            velocity. The theoretical maximal running velocity the
                                            athlete would be able to reach should mechanical
                                            resistances (ie, internal and external) against movement
                                            be null. It also represents the capability to produce
                                            horizontal force at very high running velocities."
                                                placement="top-start"
                                                TransitionComponent={Zoom}
                                            >
                                                <TableCell>V0</TableCell>
                                            </TooltipMui>
                                        </>
                                    )}
                                    <TooltipMui
                                        disableFocusListener
                                        title="Maximum speed reached during this sprint measured directly from GPS unit."
                                        placement="top-start"
                                        TransitionComponent={Zoom}
                                    >
                                        <TableCell sx={{ fontWeight: 'bold' }}>Vmax</TableCell>
                                    </TooltipMui>
                                    {sprint.f_0 && (
                                        <>
                                            <TooltipMui
                                                disableFocusListener
                                                title="Direct measurement of the proportion of the total force
                                            production that is directed in the forward direction of
                                            motion, ie, the mechanical effectiveness of force
                                            application of the athlete. The higher the value, the
                                            more important the part of the total force output
                                            directed forward."
                                                placement="top-start"
                                                TransitionComponent={Zoom}
                                            >
                                                <TableCell>RF</TableCell>
                                            </TooltipMui>
                                            <TooltipMui
                                                disableFocusListener
                                                title="Describes the athlete’s capability to limit the
                                            inevitable decrease in mechanical effectiveness with
                                            increasing speed, ie, an index of the ability to
                                            maintain a net horizontal force production despite
                                            increasing running velocity. The more negative the
                                            slope, the faster the loss of effectiveness of force
                                            application during acceleration, and vice versa."
                                                placement="top-start"
                                                TransitionComponent={Zoom}
                                            >
                                                <TableCell>DRF</TableCell>
                                            </TooltipMui>
                                        </>
                                    )}
                                    <TooltipMui
                                        disableFocusListener
                                        title="Total distance traveled during the sprint"
                                        placement="top-start"
                                        TransitionComponent={Zoom}
                                    >
                                        <TableCell>Total Distance</TableCell>
                                    </TooltipMui>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    {sprint.f_0 && (
                                        <>
                                            <TableCell>
                                                {sprint.f_0 && <>{sprint.f_0}N/kg</>}
                                                {!sprint.f_0 && <>N/A</>}
                                            </TableCell>
                                            <TableCell>
                                                {sprint.p_max && <>{sprint.p_max}W/kg</>}
                                                {!sprint.p_max && <>N/A</>}
                                            </TableCell>
                                            <TableCell>
                                                {sprint.v_0 && (
                                                    <>
                                                        {convertMpsToPreferred(
                                                            sprint.v_0,
                                                            speedUnit,
                                                        ).toFixed(2)}{' '}
                                                        {speedLabel}
                                                    </>
                                                )}
                                                {!sprint.v_0 && <>N/A</>}
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell>
                                        {convertMpsToPreferred(sprint.v_max, speedUnit).toFixed(2)}{' '}
                                        {speedLabel}
                                    </TableCell>
                                    {sprint.f_0 && (
                                        <>
                                            <TableCell>
                                                {sprint.rf_max && (
                                                    <>{Math.round(sprint.rf_max * 100)}%</>
                                                )}
                                                {!sprint.rf_max && <>N/A</>}
                                            </TableCell>
                                            <TableCell>
                                                {sprint.drf && <>{Math.round(sprint.drf * 100)}%</>}
                                                {!sprint.drf && <>N/A</>}
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell>
                                        {!sprint.total_distance_traveled && '-'}
                                        {sprint.total_distance_traveled &&
                                            displayDistanceByUnit(
                                                sprint.total_distance_traveled,
                                                distanceUnit,
                                            )}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {sprint.avg_velocity_grouped_by_second_data.length > 0 && (
                        <TableContainer
                            sx={{
                                backgroundColor: '#D9DADF',
                                borderRadius: 4,
                                border: '1px solid #A7ABB7',
                                background:
                                    'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            Time Segment:
                                        </TableCell>
                                        {sprint.avg_velocity_grouped_by_second_data.map(
                                            (r: any, index: number) => (
                                                <TableCell sx={{ fontWeight: 'bold' }} key={index}>
                                                    {r['Adjusted Time Rounded (s)']}-
                                                    {r['Adjusted Time Rounded (s)'] + 0.99}s
                                                </TableCell>
                                            ),
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow sx={{ borderBottom: '1.5px solid #A7ABB7' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            Avg Speed:
                                        </TableCell>
                                        {sprint.avg_velocity_grouped_by_second_data.map(
                                            (r: any, index: number) => (
                                                <TableCell key={index}>
                                                    {convertMpsToPreferred(
                                                        r['Velocity Filtered (m/s)'],
                                                        speedUnit,
                                                    ).toFixed(2)}{' '}
                                                    {speedLabel}
                                                </TableCell>
                                            ),
                                        )}
                                    </TableRow>
                                    <TableRow sx={{ borderBottom: '1.5px solid #A7ABB7' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Distance:</TableCell>
                                        {sprint.distance_grouped_by_second.map(
                                            (r: any, index: number) => (
                                                <TableCell key={index}>
                                                    {displayDistanceByUnit(
                                                        r['Distance (m)'],
                                                        distanceUnit,
                                                    )}
                                                </TableCell>
                                            ),
                                        )}
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}
            {/*Full Screen Modal*/}
            {isMobileDevice && (
                <>
                    <Button variant="outlined" onClick={handleClickOpen}>
                        Full Screen Mobile Graph - Turn Phone Sideways
                    </Button>
                    <Dialog
                        fullScreen
                        open={openModal}
                        TransitionComponent={Transition}
                        aria-describedby="alert-dialog-slide-description"
                    >
                        <DialogContent>
                            <ResponsiveContainer>
                                <ComposedChart
                                    data={data}
                                    margin={{
                                        top: 4,
                                        right: 10,
                                        left: 0,
                                        bottom: 0,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        type={'number'}
                                        scale={'linear'}
                                        allowDataOverflow={true}
                                        name="Time"
                                        dataKey="Adjusted Time (s)"
                                    />
                                    <YAxis
                                        type={'number'}
                                        domain={[0, YaxisMax]}
                                        allowDataOverflow={true}
                                    />
                                    <Tooltip
                                        payload={[]}
                                        contentStyle={{
                                            background:
                                                'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                            border: '1px solid #a8bfd0',
                                            borderRadius: 15,
                                        }}
                                    />
                                    <Legend verticalAlign="top" height={20} />
                                    <Bar
                                        name={distanceKey}
                                        type="monotone"
                                        dataKey={distanceKey}
                                        fill="#7794C5"
                                        barSize={0.2}
                                    />
                                    <Scatter
                                        name={`Speed (${speedLabel})`}
                                        dataKey="Velocity (m/s)"
                                        fill="#25C3F1"
                                    />
                                    {sprint.drf && !hideFVP && (
                                        <>
                                            <Line
                                                name={`Speed (${speedLabel})`}
                                                type="monotone"
                                                dataKey="Model Velocity (m/s)"
                                                stroke="#8884d8"
                                            />
                                            <Line
                                                name="Force (N/kg)"
                                                type="monotone"
                                                dataKey="Model Force (N/kg)"
                                                stroke="#0335fc"
                                            />
                                            <Line
                                                name="Power (W/kg)"
                                                type="monotone"
                                                dataKey="Model Power (W/kg)"
                                                stroke="#341f36"
                                            />
                                        </>
                                    )}
                                </ComposedChart>
                            </ResponsiveContainer>
                            <Grid container direction="row" alignItems="flex-start">
                                <Grid item>
                                    <Button onClick={handleCloseModal}>Exit</Button>
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </Dialog>
                </>
            )}
            <Grid container>
                <Grid
                    xs={12}
                    sm={decelData.length > 0 && sideBySide ? 6 : 12}
                    item
                    style={{
                        padding: 8,
                        paddingRight: 8,
                        paddingLeft: 8,
                        paddingBottom: 8,
                    }}
                >
                    <ChartGradientPaper>
                        <ResponsiveContainer>
                            <ComposedChart
                                data={data}
                                margin={{
                                    top: 10,
                                    right: 10,
                                    left: 10,
                                    bottom: 20,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type={'number'}
                                    name="Time"
                                    scale={'linear'}
                                    dataKey="Adjusted Time (s)"
                                    padding={{
                                        left: 20,
                                        right: 20,
                                    }}
                                    label={{
                                        value: 'Time (s)',
                                        angle: 0,
                                        position: 'bottom',
                                        textColor: '#A7ABB7',
                                        fontSize: 14,
                                    }}
                                />
                                <YAxis
                                    type={'number'}
                                    domain={[0, YaxisMax]}
                                    allowDataOverflow={true}
                                    label={{
                                        value: `Speed (${speedLabel})`,
                                        angle: -90,
                                        position: 'insideLeft',
                                        textColor: '#A7ABB7',
                                        fontSize: 12,
                                    }}
                                />
                                <Tooltip
                                    payload={[]}
                                    contentStyle={{
                                        background:
                                            'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                        border: '1px solid #a8bfd0',
                                        borderRadius: 15,
                                    }}
                                />
                                <Legend verticalAlign="top" height={20} />
                                <Line
                                    name={distanceKey}
                                    type="monotone"
                                    dataKey={distanceKey}
                                    fill="#7794C5"
                                />
                                <Scatter
                                    name={`Speed (${speedLabel})`}
                                    dataKey="Velocity (m/s)"
                                    fill="#25C3F1"
                                />
                                {sprint.drf && !hideFVP && (
                                    <>
                                        <Line
                                            name={`Model Speed (${speedLabel})`}
                                            type="monotone"
                                            dataKey="Model Velocity (m/s)"
                                            stroke="#8884d8"
                                        />
                                        <Line
                                            name="Force (N/kg)"
                                            type="monotone"
                                            dataKey="Model Force (N/kg)"
                                            stroke="#0335fc"
                                        />
                                        <Line
                                            name="Power (W/kg)"
                                            type="monotone"
                                            dataKey="Model Power (W/kg)"
                                            stroke="#341f36"
                                        />
                                    </>
                                )}
                            </ComposedChart>
                        </ResponsiveContainer>
                    </ChartGradientPaper>
                </Grid>
                {decelData.length > 0 && (
                    <Grid
                        xs={12}
                        sm={sideBySide ? 6 : 12}
                        item
                        style={{
                            padding: 8,
                            paddingRight: 8,
                            paddingLeft: 8,
                            paddingBottom: 8,
                        }}
                    >
                        <Paper
                            style={{
                                width: '100%',
                                height: 400,
                                backgroundColor: '#D9DADF',
                                borderRadius: 20,
                                border: '1px solid #A7ABB7',
                                background:
                                    'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                padding: 20,
                            }}
                        >
                            <ResponsiveContainer>
                                <ComposedChart
                                    data={decelData}
                                    margin={{
                                        top: 5,
                                        right: 30,
                                        left: 20,
                                        bottom: 20,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        type={'number'}
                                        scale={'linear'}
                                        dataKey="Adjusted Time (s)"
                                        label={{
                                            value: 'Time (s)',
                                            angle: 0,
                                            position: 'bottom',
                                            color: '#A7ABB7',
                                            fontSize: 14,
                                        }}
                                    />
                                    <YAxis
                                        type={'number'}
                                        domain={[0, YaxisMax]}
                                        allowDataOverflow={true}
                                        label={{
                                            value: `Speed (${speedLabel})`,
                                            angle: -90,
                                            position: 'insideLeft',
                                            color: '#A7ABB7',
                                            fontSize: 12,
                                        }}
                                    />
                                    <Tooltip
                                        payload={[]}
                                        contentStyle={{
                                            background:
                                                'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                            border: '1px solid #a8bfd0',
                                            borderRadius: 15,
                                        }}
                                    />
                                    <Legend verticalAlign="top" height={20} />
                                    <Line
                                        name={distanceKey}
                                        type="monotone"
                                        dataKey={distanceKey}
                                        fill="#A2C9FF"
                                    />
                                    <Scatter
                                        name={`Deceleration Speed (${speedLabel})`}
                                        dataKey="Velocity Filtered (m/s)"
                                        fill="#25C3F1"
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                )}
                {sprint.drf && !hideFVP && (
                    <Grid xs={12} item style={{ padding: 8 }}>
                        <Paper
                            style={{
                                width: '100%',
                                height: 400,
                                backgroundColor: '#D9DADF',
                                borderRadius: 20,
                                border: '1px solid #A7ABB7',
                                background:
                                    'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                padding: 20,
                            }}
                        >
                            <ResponsiveContainer>
                                <ScatterChart
                                    width={500}
                                    height={300}
                                    data={data}
                                    margin={{
                                        top: 5,
                                        right: 30,
                                        left: 20,
                                        bottom: 20,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="Model Velocity (m/s)"
                                        label={{
                                            value: 'Model Velocity (m/s)',
                                            angle: 0,
                                            position: 'bottom',
                                            color: '#A7ABB7',
                                            fontSize: 14,
                                        }}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        label={{
                                            value: 'Force (N)',
                                            angle: -90,
                                            position: 'insideLeft',
                                            color: '#A7ABB7',
                                            fontSize: 12,
                                        }}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        label={{
                                            value: 'Power (W/kg)',
                                            angle: -90,
                                            position: 'insideTopRight',
                                            color: '#A7ABB7',
                                            fontSize: 12,
                                        }}
                                    />
                                    <Tooltip
                                        payload={[]}
                                        contentStyle={{
                                            background:
                                                'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                            border: '1px solid #a8bfd0',
                                            borderRadius: 15,
                                        }}
                                    />
                                    <Legend verticalAlign="top" height={20} />
                                    <Scatter
                                        name="Force (N)"
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="Model Force (N)"
                                    />
                                    <Scatter
                                        name="Power (W/kg)"
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="Model Power (W/kg)"
                                        fill="#25C3F1"
                                    />
                                </ScatterChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                )}
                {sprint.drf && !hideFVP && (
                    <Grid xs={12} item style={{ padding: 8 }}>
                        <Paper
                            style={{
                                width: '100%',
                                height: 400,
                                backgroundColor: '#D9DADF',
                                borderRadius: 20,
                                border: '1px solid #A7ABB7',
                                background:
                                    'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                padding: 20,
                            }}
                        >
                            <ResponsiveContainer>
                                <ScatterChart
                                    width={500}
                                    height={300}
                                    data={data}
                                    margin={{
                                        top: 5,
                                        right: 30,
                                        left: 20,
                                        bottom: 20,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="Model Velocity (m/s)"
                                        label={{
                                            value: 'Model Velocity (m/s)',
                                            angle: 0,
                                            position: 'bottom',
                                            color: '#A7ABB7',
                                            fontSize: 14,
                                        }}
                                    />
                                    <YAxis />
                                    <Tooltip
                                        payload={[]}
                                        contentStyle={{
                                            background:
                                                'linear-gradient(to bottom, rgba(0,0,0,.011), rgba(217,218,223,.59))',
                                            border: '1px solid #a8bfd0',
                                            borderRadius: 15,
                                        }}
                                    />
                                    <Legend verticalAlign="top" height={20} />
                                    <Scatter
                                        name="Ratio Of Force (%)"
                                        type="monotone"
                                        dataKey="Model Ratio Force (N)"
                                    />
                                </ScatterChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                )}
            </Grid>
            {sprint.drf && !hideFVP && (
                <Box p={8}>
                    <dl>
                        <div>
                            <dt>
                                <Typography>F0/kg</Typography>
                            </dt>
                            <dd>
                                Maximal force output (per unit body mass) in the horizontal
                                direction. Corresponds to the initial push of the athlete onto the
                                ground during sprint acceleration. The higher the value, the higher
                                the sprint-specific horizontal force production.
                            </dd>
                        </div>
                        <div>
                            <dt>
                                <Typography>Pmax</Typography>
                            </dt>
                            <dd>
                                Maximal power-output capability of the athlete in the horizontal
                                direction (per unit body mass) during sprint acceleration.
                            </dd>
                        </div>
                        <div>
                            <dt>
                                <Typography>V0</Typography>
                            </dt>
                            <dd>
                                Sprint-running maximal velocity capability of the athlete. Slightly
                                higher than the actual maximal velocity. The theoretical maximal
                                running velocity the athlete would be able to reach should
                                mechanical resistances (ie, internal and external) against movement
                                be null. It also represents the capability to produce horizontal
                                force at very high running velocities.
                            </dd>
                        </div>
                        <div>
                            <dt>
                                <Typography>Vmax</Typography>
                            </dt>
                            <dd>Maximum speed reached during this sprint.</dd>
                        </div>
                        <div>
                            <dt>
                                <Typography>RF</Typography>
                            </dt>
                            <dd>
                                Direct measurement of the proportion of the total force production
                                that is directed in the forward direction of motion, ie, the
                                mechanical effectiveness of force application of the athlete. The
                                higher the value, the more important the part of the total force
                                output directed forward.
                            </dd>
                        </div>
                        <div>
                            <dt>
                                <Typography>DRF</Typography>
                            </dt>
                            <dd>
                                Describes the athlete’s capability to limit the inevitable decrease
                                in mechanical effectiveness with increasing speed, ie, an index of
                                the ability to maintain a net horizontal force production despite
                                increasing running velocity. The more negative the slope, the faster
                                the loss of effectiveness of force application during acceleration,
                                and vice versa.
                            </dd>
                        </div>
                    </dl>
                </Box>
            )}
        </>
    );
}

export default React.memo(ForceVelocityProfileSprint);
